import { _ } from "../../../../core/polyglot/polyglot.js";
import AbstractValueRowTemplate from "./AbstractValueRowTemplate.js";
import formulaFactory from "../../../keyFigure/formula/formulaFactory.js";
import FormulaRow from "../../reports/rows/FormulaRow.js";
import NaFormulaNode from "../../../keyFigure/formula/NaFormulaNode.js";
import TranslatedString from "../../../TranslatedString.js";

/**
 * Formula row template.
 *
 * A formula row simply holds a `formulaNode`.
 *
 * If the formula (or one node in the formula) is an `naFormulaNode`, the
 * row is considered invalid.
 *
 * Invalid rows are pruned from the data sent to the server for calculation.
 *
 * Most of the time, the formula will have `reportReferenceFormulaNode` as
 * some of its components. These nodes are resolved using `resolveReferences`.
 *
 * @param {TranslatedString} [spec.title] - Title of the formula row.
 * @param {formulaNode} [spec.formula] - Formula of the row.
 * @param {boolean} [spec.formatInPercentage] - Whether data should be
 * formatted in percentage.
 * @param {string} [spec.style] - Style used to display the row.
 */
export default class FormulaRowTemplate extends AbstractValueRowTemplate {
	constructor({
		title = new TranslatedString(),
		formula,
		formatInPercentage,
		style = "text",
	} = {}) {
		super(...arguments);

		this._title = title;
		this._formula = formula || new NaFormulaNode();
		this._formatInPercentage = formatInPercentage;
		this._style = style;
	}

	getTitle() {
		return this._title;
	}

	setTitle(title) {
		this._title = title;
	}

	getStyle() {
		return this._style;
	}

	setStyle(style) {
		this._style = style;
	}

	getFormula() {
		return this._formula;
	}

	setFormula(formula) {
		this._formula = formula;
	}

	getFormatInPercentage() {
		return this._formatInPercentage;
	}

	setFormatInPercentage(formatInPercentage) {
		this._formatInPercentage = formatInPercentage;
	}

	/**
	 * Resolve report template item references.
	 *
	 * This step is performed only when the template data structure is
	 * built, so that all references within the report can be found.
	 */
	resolveReferences() {
		this._formula.forEach((node) => {
			node.resolveReferences(this._reportTemplate);
		});
	}

	validate({ collector }) {
		super.validate(...arguments);

		collector.addErrorIf(
			this.hasRecursiveReference(),
			_("The formula row '{rowName}' references itself", {
				rowName: this.getTitle(),
			}),
		);

		this._formula.validate(...arguments);
	}

	hasRecursiveReference() {
		return [this, ...this.getPredecessors()].some((row) =>
			this._formula.hasReferenceTo(row),
		);
	}

	canBeRepeated() {
		return false;
	}

	hasDependencyTo(item) {
		return this._formula.hasReferenceTo(item);
	}

	accept(visitor) {
		return visitor.visitFormulaRowTemplate(this);
	}

	_instanceClass() {
		return FormulaRow;
	}

	_interpretationSpec() {
		const spec = super._interpretationSpec(...arguments);

		spec.title = this._title;
		spec.formatInPercentage = this._formatInPercentage;
		spec.formula = this._formula;
		spec.style = this._style;

		return spec;
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.isFormattedInPercentage = this._formatInPercentage;
		properties.title = this._title;
		properties.formula = this._formula;
		properties.style = this._style;

		return properties;
	}

	static getLabel() {
		return _("Formula");
	}

	static getType() {
		return "FormulaRowTemplate";
	}

	static transformMaterializationData(data) {
		super.transformMaterializationData(...arguments);

		data.formula &&= formulaFactory.materialize(data.formula);
		data.formatInPercentage = data.isFormattedInPercentage;
		data.title = TranslatedString.materialize(data.title);
	}
}
